import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Comment } from '../types';
import { MessageSquare, Send, User, Clock, AlertCircle } from 'lucide-react';

interface CommentsSectionProps {
  articleId: string;
}

export const CommentsSection: React.FC<CommentsSectionProps> = ({ articleId }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    authorName: '',
    content: ''
  });

  const fetchComments = async () => {
    try {
      setIsLoading(true);
      const data = await api.get<Comment[]>(`/articles/${articleId}/comments`);
      setComments(data);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
      setError('Could not load comments. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (articleId) {
      fetchComments();
    }
  }, [articleId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.authorName.trim() || !formData.content.trim()) return;

    try {
      setIsSubmitting(true);
      setError(null);
      const newComment = await api.post<Comment>(`/articles/${articleId}/comments`, formData);
      setComments([newComment, ...comments]);
      setFormData({ authorName: '', content: '' });
    } catch (err) {
      console.error('Failed to post comment:', err);
      setError('Failed to post your comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mt-12 pt-12 border-t border-gray-100 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  return (
    <div className="mt-12 pt-12 border-t border-gray-100">
      <div className="flex items-center gap-2 mb-8">
        <MessageSquare className="text-brand-500" size={24} />
        <h2 className="text-2xl font-serif font-bold text-brand-900">
          Comments ({comments.length})
        </h2>
      </div>

      {/* Comment Form */}
      <form onSubmit={handleSubmit} className="mb-12 bg-gray-50 p-4 md:p-6 rounded-xl border border-gray-100">
        <h3 className="text-lg font-bold text-brand-900 mb-4 font-serif">Leave a Comment</h3>
        
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label htmlFor="authorName" className="block text-sm font-medium text-gray-700 mb-1 font-sans">
              Your Name
            </label>
            <input
              type="text"
              id="authorName"
              required
              value={formData.authorName}
              onChange={(e) => setFormData({ ...formData, authorName: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all font-sans"
              placeholder="e.g. John Doe"
            />
          </div>
          
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1 font-sans">
              Comment
            </label>
            <textarea
              id="content"
              required
              rows={4}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all resize-none font-sans"
              placeholder="Share your thoughts..."
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting || !formData.authorName.trim() || !formData.content.trim()}
              className="flex items-center gap-2 bg-brand-900 text-white px-6 py-2 rounded-lg font-bold hover:bg-brand-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-sans"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Posting...</span>
                </>
              ) : (
                <>
                  <Send size={18} />
                  <span>Post Comment</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Comments List */}
      <div className="space-y-6 md:space-y-8">
        {comments.length === 0 ? (
          <p className="text-center text-gray-500 py-8 italic font-serif">
            No comments yet. Be the first to share your thoughts!
          </p>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="flex gap-3 md:gap-4 group">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center text-brand-500 shadow-sm border border-brand-200">
                  <User size={20} />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-brand-900 font-serif">{comment.authorName}</span>
                  <span className="text-[10px] md:text-xs text-gray-400 flex items-center gap-1 font-sans">
                    <Clock size={12} />
                    {new Date(comment.createdAt).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm group-hover:border-brand-200 transition-colors">
                  <p className="text-gray-700 leading-relaxed font-sans text-sm md:text-base">
                    {comment.content}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
